import {SortingDirection, UserSortField} from '../types/__generated__/graphql';
import type {UserFilter, UserSort} from '../types/__generated__/graphql';

/**
 * The Users page keeps its filters in the URL so a filtered list can be bookmarked and shared.
 * These helpers turn those URL parameters into the server's UserFilter / UserSort. Unknown values
 * are ignored rather than guessed at.
 */

const DAY = 24 * 60 * 60 * 1000;

/** Yes/no URL parameters and the UserFilter field each one sets. */
export const FLAG_FILTERS = [
  {param: 'admin', field: 'systemAdmin', label: 'System admin'},
  {param: 'early', field: 'earlyAccessProgram', label: 'Early access'},
  {param: 'terms', field: 'acceptedTerms', label: 'Accepted terms'},
  {param: 'verified', field: 'emailValid', label: 'Email verified'},
  {param: 'sso', field: 'isSSO', label: 'SSO'},
  {param: 'agent', field: 'isAgent', label: 'Agent'},
] as const;

export const ORG_PARAM = 'org';
export const LOGIN_PARAM = 'login';
export const CREATED_PARAM = 'created';
export const SORT_PARAM = 'sort';

export const LOGIN_PRESETS = {
  never: {label: 'Never logged in'},
  '30d': {label: 'Logged in last 30 days'},
  inactive90d: {label: 'Inactive 90+ days'},
  inactive365d: {label: 'Inactive 1+ year'},
} as const;

export const CREATED_PRESETS = {
  '7d': {label: 'Last 7 days', days: 7},
  '30d': {label: 'Last 30 days', days: 30},
  '90d': {label: 'Last 90 days', days: 90},
  '365d': {label: 'Last year', days: 365},
} as const;

export const SORT_PRESETS = {
  username: {label: 'Email (A–Z)', field: UserSortField.Username, direction: SortingDirection.Asc},
  name: {label: 'Name (A–Z)', field: UserSortField.Name, direction: SortingDirection.Asc},
  newest: {label: 'Newest first', field: UserSortField.CreatedAt, direction: SortingDirection.Desc},
  oldest: {label: 'Oldest first', field: UserSortField.CreatedAt, direction: SortingDirection.Asc},
  recentLogin: {label: 'Recent login first', field: UserSortField.LastLogin, direction: SortingDirection.Desc},
  staleLogin: {label: 'Oldest login first', field: UserSortField.LastLogin, direction: SortingDirection.Asc},
} as const;

// `filter` is the retired enabled/disabled tab; it is cleared along with the real filters.
const FILTER_PARAMS = [...FLAG_FILTERS.map((f) => f.param), ORG_PARAM, LOGIN_PARAM, CREATED_PARAM];
const RETIRED_PARAMS = ['filter'];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const own = <T extends object>(record: T, key: string | null): key is Extract<keyof T, string> =>
  key !== null && Object.prototype.hasOwnProperty.call(record, key);

/** Relative presets are anchored to the hour so the query variables, and the Apollo cache, stay stable. */
export function startOfHour(time: number): number {
  return time - (time % (60 * 60 * 1000));
}

function flagValue(value: string | null): boolean | undefined {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
}

export function filterFromParams(params: URLSearchParams, now: number): UserFilter | undefined {
  const filter: UserFilter = {};

  for (const {param, field} of FLAG_FILTERS) {
    const value = flagValue(params.get(param));
    if (value !== undefined) filter[field] = value;
  }

  const org = params.get(ORG_PARAM);
  if (org === 'none') filter.withoutOrganisation = true;
  else if (org === 'some') filter.withoutOrganisation = false;
  else if (org && UUID.test(org)) filter.organisationId = org;

  const login = params.get(LOGIN_PARAM);
  if (own(LOGIN_PRESETS, login)) {
    if (login === 'never') filter.neverLoggedIn = true;
    else if (login === '30d') filter.lastLoginAfter = now - 30 * DAY;
    else if (login === 'inactive90d') filter.lastLoginBefore = now - 90 * DAY;
    else filter.lastLoginBefore = now - 365 * DAY;
  }

  const created = params.get(CREATED_PARAM);
  if (own(CREATED_PRESETS, created)) filter.createdAfter = now - CREATED_PRESETS[created].days * DAY;

  return Object.keys(filter).length > 0 ? filter : undefined;
}

export function sortFromParams(params: URLSearchParams): UserSort | undefined {
  const sort = params.get(SORT_PARAM);
  if (!own(SORT_PRESETS, sort)) return undefined;
  const {field, direction} = SORT_PRESETS[sort];
  return {field, direction};
}

export function countActiveFilters(params: URLSearchParams): number {
  const single = (key: string) => new URLSearchParams(params.has(key) ? {[key]: params.get(key)!} : {});
  return FILTER_PARAMS.filter((key) => filterFromParams(single(key), 0) !== undefined).length;
}

export function clearedFilterParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  [...FILTER_PARAMS, ...RETIRED_PARAMS, 'page'].forEach((key) => next.delete(key));
  return next;
}
